import { Model, UIRadio, UISelect, UITable, UIText } from "@alphascriptsolutions/client-core";
import { useSharePricePerformanceStore } from "../../state/sharePricePerformanceStore";
import {
  setRangeInputDate,
  setTableDataWithDropdown,
  setTableDataWithRemove,
  updateModelAt,
  updateUIOptions,
} from "./panelHelpers";
import { ModelIndexes } from "../../types/addContent";
import { removeDynamicSchemasUpTo } from "./panelsSchema";

export const updateTableDatasource = (model: Model): boolean => {
  const { company, models, setCompany } = useSharePricePerformanceStore.getState();

  const tablePanel = model.uiComponents[0].getByName<UITable>("companiesOnChartTable");
  setTableDataWithRemove(tablePanel, company, (id) => {
    const updatedCompanies = company.filter((c) => c.id !== id);
    setCompany(updatedCompanies);
  });
  setTableDataWithDropdown(tablePanel, company, (newValue, id) => {
    const idNew = id.split("-")[1];
    const numericId = Number(idNew);
    const updatedCompanies = company.map((rec) =>
      rec.id === numericId ? { ...rec, dataType: `${newValue}-${idNew}` } : rec
    );
    setCompany(updatedCompanies);
  });

  const companyNames = company.map((c) => c.companyNameAndTicker);

  updateUIOptions([models[ModelIndexes.INDEXATION_MODEL_INDEX].getByName("companyNames") as UIRadio], companyNames);
  updateUIOptions(
    models[ModelIndexes.CONSTANT_LINE].uiComponents[0].getByPartialName("radioCompanyNamesPointInTime") as UISelect[],
    companyNames
  );
  updateUIOptions(
    models[ModelIndexes.CONSTANT_LINE].uiComponents[0].getByPartialName("radioCompanyNamesTimeSeries") as UISelect[],
    companyNames
  );
  updateUIOptions(
    models[ModelIndexes.SUMMARY_TABLE].uiComponents[0].getByPartialName("checkboxCompaniesIncluded") as UIRadio[],
    companyNames
  );
  updateUIOptions(
    models[ModelIndexes.SMALL_LINE_CHART].uiComponents[0].getByPartialName("checkboxCompaniesIncluded") as UIRadio[],
    companyNames
  );

  updateModelAt(ModelIndexes.INDEXATION_MODEL_INDEX, () => models);
  updateModelAt(ModelIndexes.CONSTANT_LINE, () => models);
  updateModelAt(ModelIndexes.SUMMARY_TABLE, () => models);
  updateModelAt(ModelIndexes.SMALL_LINE_CHART, () => models);

  return true;
};

export const updateTestTableDatasource = (model: Model): boolean => {
  const { models, summaryTable, setSummaryTable } = useSharePricePerformanceStore.getState();
  const periodSummaryTablePanel = model.uiComponents[0].getByName<UITable>("periodSummaryTable");
  if (!periodSummaryTablePanel) return false;

  setTableDataWithRemove(periodSummaryTablePanel, summaryTable, (id) => {
    setSummaryTable(summaryTable.filter((company) => company.id !== id));
  });

  const tableModel = models[ModelIndexes.TABLE_MODEL_INDEX];
  const addCustom = !!tableModel.getByName("addCustomBuiltIndices")?.value;
  const customIndexText = model.getByName<UIText>("textCustomBuildIndex");
  const customIndicesIncluded = model.getByName<UIRadio>("customIndicesIncluded");

  if (addCustom) {
    customIndexText.text = "<p><b>Custom Built Indices to include:</b></p>";
    const indexFields = models[ModelIndexes.CUSTOM_BUILD_INDEX].uiComponents[0].getByPartialName("indexName");
    customIndicesIncluded.options = indexFields.map((f) => f.value);
  } else {
    customIndexText.text = "";
    customIndicesIncluded.options = [];
    removeDynamicSchemasUpTo(model, ["customIndicesIncluded"]);
  }

  return true;
};

export const updateSmallLineChartData = (model: Model): boolean => {
  const { models } = useSharePricePerformanceStore.getState();

  setRangeInputDate(models[ModelIndexes.SMALL_LINE_CHART], "smallLineChartTimePeriod");

  const tableModel = models[ModelIndexes.TABLE_MODEL_INDEX];
  const addCustom = !!tableModel.getByName("addCustomBuiltIndices")?.value;
  const addConstant = !!tableModel.getByName("addConstantLineSelection")?.value;

  const customIndexText = model.getByName<UIText>("textCustomBuildIndex");
  const customIndicesIncluded = model.getByName<UIRadio>("customIndicesIncludedSmallLine");

  if (addCustom) {
    customIndexText.text = "<p><b>Custom Built Indices to include:</b></p>";
    const indexFields = models[ModelIndexes.CUSTOM_BUILD_INDEX].uiComponents[0].getByPartialName("indexName");
    customIndicesIncluded.options = indexFields.map((f) => f.value);
  } else {
    customIndexText.text = "";
    customIndicesIncluded.options = [];
    removeDynamicSchemasUpTo(model, ["customIndicesIncludedSmallLine"]);
  }

  const constantText = model.getByName<UIText>("textConstantLine");
  const constLinesIncluded = model.getByName<UIRadio>("constantLinesIncludedSmallLine");

  if (addConstant) {
    constantText.text = "<p><b>Include Constant Value Lines:</b></p>";
    const lineFields = models[ModelIndexes.CONSTANT_LINE].uiComponents[0].getByPartialName("lineName");
    constLinesIncluded.options = lineFields.map((f) => f.value);
  } else {
    constantText.text = "";
    constLinesIncluded.options = [];
    removeDynamicSchemasUpTo(model, ["constantLinesIncludedSmallLine"]);
  }

  return true;
};

export const updateCallOutBoxesTableDatasource = (model: Model): boolean => {
  const { callOutBoxTable, setCallOutBoxTable } = useSharePricePerformanceStore.getState();

  const table = model.uiComponents[0].getByName<UITable>("callOutBoxesTable");
  if (!table) return false;

  setTableDataWithRemove(table, callOutBoxTable, (id) => {
    setCallOutBoxTable(callOutBoxTable.filter((company) => company.id !== id));
  });

  return true;
};

export const updateShadingPeriodTableDatasource = (model: Model): boolean => {
  const { shadingPeriodTable, setShadingPeriodTable } = useSharePricePerformanceStore.getState();

  const tablePanel = model.uiComponents[0].getByName<UITable>("shadingPeriodTable");
  if (!tablePanel) return false;

  setTableDataWithRemove(tablePanel, shadingPeriodTable, (id) => {
    setShadingPeriodTable(shadingPeriodTable.filter((company) => company.id !== id));
  });

  return true;
};

export const updateVerticalLineTableDatasource = (model: Model): boolean => {
  const { verticalLineTable, setVerticalLineTable } = useSharePricePerformanceStore.getState();

  const tablePanel = model.uiComponents[0].getByName<UITable>("verticalLineTable");
  if (!tablePanel) return false;

  setTableDataWithRemove(tablePanel, verticalLineTable, (id) => {
    setVerticalLineTable(verticalLineTable.filter((company) => company.id !== id));
  });

  return true;
};

export const updateAPALTableDatasource = (model: Model): boolean => {
  const { APALCompany, setAPALCompany } = useSharePricePerformanceStore.getState();
  APALCompany.forEach((table, index) => {
    if (!table) return;

    const tablePanel = model.uiComponents[0].getByName<UITable>("defineIndexContituents" + (index > 0 ? index : ""));
    if (!tablePanel) return;

    setTableDataWithRemove(tablePanel, table, (id: string) => {
      const newState = [...APALCompany];
      const tableIndexFromId = parseInt(id.split("-")[0]);
      if (newState[tableIndexFromId]) {
        newState[tableIndexFromId] = newState[tableIndexFromId].filter((company) => company.hiddenId !== id);
      }
      setAPALCompany(newState);
    });
  });

  return true;
};
